import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import axios from 'axios';
import { useAppDispatch } from 'app/config/store';
import { getEntities as getDepositEntities } from 'app/entities/deposit-transaction/deposit-transaction.reducer';
import { getAccount } from 'app/shared/reducers/authentication';
import Swal from 'sweetalert2';
import { colors } from 'app/shared/theme/colors';
import { useTranslation } from 'app/shared/i18n';

const pulseKeyframes = `
@keyframes depositPulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.08); opacity: 0.85; }
}
@keyframes depositSpin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
@keyframes depositDots {
  0%, 20% { content: '.'; }
  40% { content: '..'; }
  60%, 100% { content: '...'; }
}
@keyframes depositGlow {
  0%, 100% { box-shadow: 0 0 8px rgba(99, 102, 241, 0.3); }
  50% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.6), 0 0 40px rgba(99, 102, 241, 0.2); }
}
@keyframes timerPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
`;

export const DepositCreatedDialog = props => {
  const { t } = useTranslation();
  const [isLoading, setLoading] = useState(true);
  const [status, setStatus] = useState(false);
  const [code, setCode] = useState('');
  // Nội dung chuyển khoản thân thiện user copy (thay cho code); code vẫn giữ ngầm để poll trạng thái.
  const [transferContent, setTransferContent] = useState('');
  const [imgData, setImgData] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [expireDate, setExpireDate] = useState<Date | null>(null);
  const [remaining, setRemaining] = useState<number>(0);
  const [expired, setExpired] = useState(false);
  const dispatch = useAppDispatch();

  // Reset stale state when dialog opens with new props
  useEffect(() => {
    if (props.isOpen) {
      setElapsed(0);
      setStatus(false);
      setImgData('');
      setCode('');
      setTransferContent('');
      setLoading(true);
      setExpireDate(null);
      setRemaining(0);
      setExpired(false);
    }
  }, [props.isOpen, props.bankId, props.amount]);

  useEffect(() => {
    if (props.isOpen && props.bankId && props.amount) createTransaction();
  }, [props.bankId, props.amount, props.isOpen]);

  const createTransaction = async () => {
    try {
      const { data } = await axios.post('/api/deposit', { bankId: props.bankId, amount: props.amount.replace(/\./g, '') });
      setCode(data.data.code);
      // Ưu tiên cụm thân thiện; nếu lệnh cũ chưa có thì lùi về code (tương thích ngược).
      const memo = data.data.transferContent || data.data.code;
      setTransferContent(memo);
      if (data.data.expireDate) {
        // Calculate expiry from server's createdDate + timeout, using relative duration
        // to avoid timezone mismatch (server sends LocalDateTime without timezone)
        if (data.data.expireDate && data.data.createdDate) {
          const created = new Date(data.data.createdDate + 'Z').getTime(); // force UTC
          const expires = new Date(data.data.expireDate + 'Z').getTime(); // force UTC
          const durationMs = expires - created; // timeout duration in ms
          setExpireDate(new Date(Date.now() + durationMs)); // apply relative to client clock
        } else if (data.data.expireDate) {
          // Fallback: append Z to force UTC parsing
          setExpireDate(new Date(data.data.expireDate + 'Z'));
        }
      }
      dispatch(getDepositEntities({ sort: 'createdDate,DESC' }));
      setImgData(
        `https://img.vietqr.io/image/${data.data.bank.bankCode}-${data.data.bank.accountNumber}-compact2.png?amount=${props.amount.replace(/\./g, '')}&addInfo=${encodeURIComponent(memo)}&accountName=${encodeURIComponent(data.data.bank.accountName)}`,
      );
      setLoading(false);
      setElapsed(0);
    } catch {
      setLoading(false);
      Swal.fire({ title: t('depositDialog.error'), text: t('depositDialog.cannotCreate'), icon: 'error' });
    }
  };

  // Countdown timer
  useEffect(() => {
    if (!expireDate) return;
    const tick = () => {
      const diff = Math.max(0, Math.floor((expireDate.getTime() - Date.now()) / 1000));
      setRemaining(diff);
      if (diff <= 0) {
        setExpired(true);
      }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [expireDate]);

  // Poll status — stop when expired or already approved
  useEffect(() => {
    if (status || !code || expired) return;
    const intervalId = setInterval(checkStatus, 5000);
    return () => clearInterval(intervalId);
  }, [status, code, expired]);

  // Elapsed timer — stop when expired or already approved
  useEffect(() => {
    if (status || !code || expired) return;
    const timer = setInterval(() => setElapsed(prev => prev + 1), 1000);
    return () => clearInterval(timer);
  }, [status, code, expired]);

  useEffect(() => {
    if (status) {
      const handleSuccess = async () => {
        await Promise.all([dispatch(getAccount()), dispatch(getDepositEntities({ sort: 'createdDate,DESC' }))]);
        if (props.handleClose) props.handleClose();
        setTimeout(
          () =>
            Swal.fire({
              title: t('depositDialog.successTitle'),
              text: t('depositDialog.successText'),
              icon: 'success',
              confirmButtonText: t('depositDialog.close'),
            }),
          300,
        );
      };
      handleSuccess();
    }
  }, [status]);

  const checkStatus = async () => {
    if (!code) return;
    try {
      const { data } = await axios.get(`api/deposit/check/${code}`);
      if (data.data.expired) {
        setExpired(true);
        return;
      }
      setStatus(data.data.isApproved);
    } catch (e) {
      /* ignore */
    }
  };

  const formatElapsed = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}:${sec.toString().padStart(2, '0')}` : `${sec}s`;
  };

  const formatRemaining = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  const getTimerColor = (s: number) => {
    if (s > 300) return colors.success;
    if (s > 120) return '#f59e0b'; // amber
    return colors.danger;
  };

  const handleCreateNew = () => {
    if (props.handleClose) props.handleClose();
  };

  return (
    <>
      <style>{pulseKeyframes}</style>
      <Modal
        isOpen={props.isOpen}
        modalTransition={{ timeout: 20 }}
        backdropTransition={{ timeout: 10 }}
        toggle={props.handleClose}
        zIndex={1060}
      >
        <ModalHeader toggle={props.handleClose} style={{ background: colors.surface, borderBottom: `1px solid ${colors.border}` }}>
          <span style={{ fontSize: 16, fontWeight: 700 }}>{`\uD83D\uDCB3 ${t('depositDialog.title')}`}</span>
        </ModalHeader>
        {isLoading ? (
          <ModalBody style={{ padding: 50, textAlign: 'center' }}>
            <div
              style={{
                width: 40,
                height: 40,
                margin: '0 auto 16px',
                border: `3px solid ${colors.border}`,
                borderTopColor: colors.accent,
                borderRadius: '50%',
                animation: 'depositSpin 0.8s linear infinite',
              }}
            />
            <div style={{ fontSize: 14, color: colors.muted }}>{t('depositDialog.creating')}</div>
          </ModalBody>
        ) : (
          <ModalBody style={{ padding: 24 }}>
            <div style={{ textAlign: 'center' }}>
              {/* Countdown timer */}
              {expireDate && !status && (
                <div
                  style={{
                    marginBottom: 16,
                    padding: '10px 16px',
                    borderRadius: 12,
                    background: expired ? `${colors.danger}10` : `${getTimerColor(remaining)}10`,
                    border: `1px solid ${expired ? `${colors.danger}33` : `${getTimerColor(remaining)}33`}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  {expired ? (
                    <>
                      <span style={{ fontSize: 18 }}>{'\u23F0'}</span>
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: 15,
                          color: colors.danger,
                        }}
                      >
                        {t('depositDialog.expired')}
                      </span>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 16 }}>{'\u23F1\uFE0F'}</span>
                      <span
                        style={{
                          fontSize: 12,
                          color: getTimerColor(remaining),
                          fontWeight: 600,
                        }}
                      >
                        {t('depositDialog.remaining')}
                      </span>
                      <span
                        style={{
                          fontWeight: 800,
                          fontSize: 20,
                          color: getTimerColor(remaining),
                          fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                          letterSpacing: '.05em',
                          ...(remaining <= 120 ? { animation: 'timerPulse 1s ease-in-out infinite' } : {}),
                        }}
                      >
                        {formatRemaining(remaining)}
                      </span>
                    </>
                  )}
                </div>
              )}

              {/* QR code — dim when expired */}
              <div
                style={{
                  position: 'relative',
                  ...(expired ? { opacity: 0.35, filter: 'grayscale(100%)' } : {}),
                }}
              >
                <img src={imgData} alt={t('depositDialog.qrAlt')} style={{ maxWidth: '100%', borderRadius: 12 }} />
              </div>

              {/* Transfer code — dim when expired */}
              <div
                style={{
                  marginTop: 16,
                  padding: '14px 18px',
                  borderRadius: 12,
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  ...(expired ? { opacity: 0.4 } : {}),
                }}
              >
                <span style={{ fontSize: 13, color: colors.muted }}>
                  {t('depositDialog.transferContent', { defaultValue: 'Nội dung chuyển khoản' })}
                </span>
                <span
                  style={{
                    fontWeight: 800,
                    color: colors.accent,
                    fontSize: 16,
                    fontFamily: "'JetBrains Mono', monospace",
                    letterSpacing: '.02em',
                  }}
                >
                  {transferContent}
                </span>
              </div>

              {/* Expired overlay message + create new button */}
              {expired && !status && (
                <div
                  style={{
                    marginTop: 16,
                    padding: '20px 24px',
                    borderRadius: 12,
                    background: `${colors.danger}10`,
                    border: `1px solid ${colors.danger}33`,
                    textAlign: 'center',
                  }}
                >
                  <div style={{ color: colors.danger, fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{t('depositDialog.expired')}</div>
                  <div style={{ color: colors.muted, fontSize: 13, marginBottom: 16 }}>{t('depositDialog.expiredDesc')}</div>
                  <button
                    onClick={handleCreateNew}
                    style={{
                      padding: '10px 28px',
                      borderRadius: 10,
                      background: colors.accent,
                      color: '#fff',
                      border: 'none',
                      fontWeight: 700,
                      fontSize: 14,
                      cursor: 'pointer',
                      transition: 'opacity 0.2s',
                    }}
                    onMouseEnter={e => {
                      (e.target as HTMLButtonElement).style.opacity = '0.85';
                    }}
                    onMouseLeave={e => {
                      (e.target as HTMLButtonElement).style.opacity = '1';
                    }}
                  >
                    {t('depositDialog.createNew')}
                  </button>
                </div>
              )}

              {/* Status indicator — only show when NOT expired */}
              {!expired && (
                <div style={{ marginTop: 20 }}>
                  {status ? (
                    <div
                      style={{
                        padding: '14px 24px',
                        borderRadius: 12,
                        background: `${colors.success}15`,
                        border: `1px solid ${colors.success}33`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10,
                      }}
                    >
                      <span style={{ fontSize: 22 }}>&#10003;</span>
                      <span style={{ color: colors.success, fontWeight: 700, fontSize: 15 }}>{t('depositDialog.success')}</span>
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: '16px 20px',
                        borderRadius: 12,
                        background: `${colors.accent}08`,
                        border: `1px solid ${colors.accent}22`,
                        animation: 'depositGlow 2s ease-in-out infinite',
                      }}
                    >
                      {/* Spinner + text */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 10 }}>
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            flexShrink: 0,
                            border: `3px solid ${colors.border}`,
                            borderTopColor: colors.accent,
                            borderRightColor: colors.accent,
                            borderRadius: '50%',
                            animation: 'depositSpin 1s linear infinite',
                          }}
                        />
                        <span
                          style={{
                            color: colors.accent,
                            fontWeight: 700,
                            fontSize: 14,
                            animation: 'depositPulse 2s ease-in-out infinite',
                          }}
                        >
                          {t('depositDialog.waiting')}
                        </span>
                      </div>

                      {/* Progress bar */}
                      <div
                        style={{
                          height: 3,
                          borderRadius: 2,
                          background: colors.border,
                          overflow: 'hidden',
                          marginBottom: 10,
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            borderRadius: 2,
                            background: `linear-gradient(90deg, ${colors.accent}, #6366f1)`,
                            width: `${Math.min((elapsed % 5) * 20, 100)}%`,
                            transition: 'width 1s linear',
                          }}
                        />
                      </div>

                      {/* Info */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 11, color: colors.dim }}>{t('depositDialog.autoCheck')}</span>
                        <span
                          style={{
                            fontSize: 11,
                            color: colors.muted,
                            fontFamily: "'JetBrains Mono', monospace",
                            background: colors.surface,
                            padding: '2px 8px',
                            borderRadius: 6,
                          }}
                        >
                          {formatElapsed(elapsed)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </ModalBody>
        )}
      </Modal>
    </>
  );
};
